import { apiClient, ApiError } from '../client';
import { API_CONFIG } from '../config';

// Meta API endpoints (these are different from the main API)
const BASE_URL = `${API_CONFIG.BASE_URL}/meta`;

export interface ExchangeTokenData {
  code: string;
  state?: string;
}

export interface CreateBusinessAccountData {
  businessAccountId: string;
  accessToken: string;
  name: string;
  timezoneId?: string;
}

export interface UpdateAccessTokenData {
  businessAccountId: string;
  accessToken: string;
}

export interface GetTemplatesParams {
  businessAccountId: string;
  limit?: number;
  offset?: number;
}

export interface UploadMediaData {
  businessAccountId: string;
  file: File;
  type?: string;
}

export interface UploadMediaUrlData {
  businessAccountId: string;
  url: string;
  type?: string;
  filename?: string;
}

export interface BusinessProfileUpdatePayload {
  about?: string;
  description?: string;
  email?: string;
  address?: string;
  vertical?: string;
  websites?: string[];
  profile_picture_handle?: string;
}

/**
 * Conversational automation (WhatsApp "conversational components").
 *
 * Meta stores these on the phone number, not the WABA:
 *   GET/POST /{phone-number-id}/conversational_automation
 *
 * - `prompts` — ice breakers. Up to 4 tappable questions shown in an empty chat.
 * - `commands` — up to 30 slash commands users can type to trigger a flow.
 */
export interface ConversationalAutomationCommand {
  command_name: string;
  command_description: string;
}

/**
 * What a typed `/` command DOES when a customer sends it.
 *
 * Currently the same three as an ice breaker, but a SEPARATE type on purpose:
 * commands and ice breakers are different features that happen to share an
 * action set today, and merging them would mean an action added to one silently
 * appearing on the other. A fourth, `agent`, was built and removed on
 * 2026-08-22.
 */
export type ConversationalAutomationCommandActionType = 'flow' | 'text' | 'url';

/**
 * One command as sent to the API.
 *
 * A superset of `ConversationalAutomationCommand`: every extra field is
 * OPTIONAL, so the historical `{ command_name, command_description }` shape is
 * still a valid payload and an older backend ignores the rest.
 *
 * `enabled: false` keeps the command in our own table and WITHHOLDS it from
 * Meta — the only way to express "off", since Meta's edge has no such flag.
 */
export interface ConversationalAutomationCommandInput {
  command_name: string;
  command_description: string;
  /** Absent means enabled — every pre-toggle caller means a live command. */
  enabled?: boolean;
  /** Absent means `flow` — the historical shape. */
  action_type?: ConversationalAutomationCommandActionType;
  /** Set when `action_type` is `flow`. */
  flow_id?: string;
  /** Set when `action_type` is `text`. */
  message?: string;
  /** Set when `action_type` is `url`. */
  url?: string;
}

/** One command as returned by the API, decorated with the linked flow. */
export interface ConversationalAutomationCommandRow {
  command_name: string;
  command_description: string;
  enabled: boolean;
  /** Null when the row carries no action — read the mode from its content. */
  action_type: ConversationalAutomationCommandActionType | null;
  flow_id: string | null;
  message: string | null;
  url: string | null;
  flow_name: string | null;
  flow_status: string | null;
  /** True when the linked flow has since been deleted — the command is inert. */
  flow_missing: boolean;
}

/**
 * What an ice breaker chip DOES when a customer taps it.
 *
 * - `flow` — run one of the account's automation flows. The only kind the
 *   backend persists today (see `ConversationalAutomationPromptInput`).
 * - `text` — reply with a single WhatsApp message, no flow involved.
 * - `url`  — reply with a message carrying a link the customer can open.
 *   WhatsApp cannot open a browser from a chip tap, so this is a link SENT,
 *   never a redirect.
 *
 * The runtime list and the type are ONE declaration: the hook validates against
 * the array and the editor renders from it, so a value in one and not the other
 * is a class of bug this shape makes impossible.
 */
export const CONVERSATIONAL_AUTOMATION_PROMPT_ACTIONS = ['flow', 'text', 'url'] as const;

export type ConversationalAutomationActionType =
  (typeof CONVERSATIONAL_AUTOMATION_PROMPT_ACTIONS)[number];

/**
 * One ice breaker as sent to the API.
 *
 * `flow_id` is the automation flow that runs when a customer taps the chip.
 * Meta itself stores only the text — the link is persisted server-side and
 * matched back on the inbound message body — so this pairing exists only on
 * our own endpoint, never on Graph.
 *
 * `action_type` / `message` / `url` describe the non-flow actions. They are
 * OPTIONAL and omitted entirely for a plain flow link, so the payload a flow
 * row produces is byte-identical to what this endpoint has always received.
 */
export interface ConversationalAutomationPromptInput {
  text: string;
  flow_id: string;
  /** Absent means `flow` — the historical shape. */
  action_type?: ConversationalAutomationActionType;
  /** Set when `action_type` is `text`. */
  message?: string;
  /** Set when `action_type` is `url`. */
  url?: string;
}

/** One ice breaker as returned by the API, decorated with the linked flow. */
export interface ConversationalAutomationPrompt {
  text: string;
  flow_id: string | null;
  flow_name: string | null;
  flow_status: string | null;
  /** True when the linked flow has since been deleted — the chip is inert. */
  flow_missing: boolean;
  /** Null when the API predates action types — read as `flow`. */
  action_type: ConversationalAutomationActionType | null;
  message: string | null;
  url: string | null;
}

export interface ConversationalAutomationPayload {
  prompts?: ConversationalAutomationPromptInput[];
  commands?: ConversationalAutomationCommandInput[];
}

export const CONVERSATIONAL_AUTOMATION_LIMITS = {
  MAX_PROMPTS: 4,
  PROMPT_MAX_LENGTH: 80,
  MAX_COMMANDS: 30,
  COMMAND_NAME_MAX_LENGTH: 32,
  COMMAND_DESCRIPTION_MAX_LENGTH: 256,
} as const;

/**
 * Order matters — this is the order the segmented control renders in, and the
 * order `runtime` validation walks.
 *
 * Currently the same three as an ice breaker. Kept as a separate literal rather
 * than a spread of the prompt actions so each list reads as the whole vocabulary
 * of its own kind at a glance — and so an action added to one does not silently
 * appear on the other, where its dispatch may not exist.
 */
export const CONVERSATIONAL_AUTOMATION_COMMAND_ACTIONS = [
  'flow',
  'text',
  'url',
] as const;

export interface UploadBusinessProfileLogoResponse {
  success: boolean;
  handle?: string;
  data?: {
    handle?: string;
    url?: string;
  };
  error?: string;
}

export interface MessagingLimitInfo {
  tier: string | null;
  limit: number | null;
  isUnlimited: boolean;
  used: number;
  remaining: number | null;
  windowStart: string | null;
  quality_rating?: string | null;
}

export interface MessagingUsagePhoneNumber {
  id: string;
  display_phone_number: string;
  status?: string | null;
  verified_name?: string | null;
  messaging_limit_tier?: string | null;
  messaging_limit_info?: MessagingLimitInfo | null;
  quality_rating?: string | null;
}

export interface MessagingUsageSummary {
  businessAccountId: string;
  phoneNumbers: MessagingUsagePhoneNumber[];
  aggregate: MessagingLimitInfo;
}

export const metaApi = {
  /**
   * Get Meta configuration
   */
  async getConfig() {
    const response = await fetch(`${BASE_URL}/get-config`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get Meta configuration');
    }

    return response.json();
  },

  /**
   * Get WhatsApp phone numbers for a stored business account (server uses stored token)
   */
  async getPhoneNumbers(businessAccountRecordId: string) {
    const response = await fetch(`${BASE_URL}/whatsapp/phone-numbers/${businessAccountRecordId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get phone numbers');
    }

    return response.json();
  },

  /**
   * Get messaging limit usage stats for a business account
   */
  async getMessagingUsage(businessAccountRecordId: string) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // BASE_URL already includes /api/v1/meta, so we just append the path
    const response = await fetch(`${BASE_URL}/whatsapp/messaging-usage/${businessAccountRecordId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get messaging usage');
    }

    return response.json();
  },

  /**
   * Exchange authorization code for access token
   */
  async exchangeToken(data: ExchangeTokenData) {
    const response = await fetch(`${BASE_URL}/exchange-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to exchange token');
    }

    return response.json();
  },

  /**
   * Get business accounts
   */
  async getBusinessAccounts(accessToken: string) {
    const response = await fetch(`${BASE_URL}/business-accounts`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get business accounts');
    }

    return response.json();
  },

  /**
   * Create business account
   */
  async createBusinessAccount(data: CreateBusinessAccountData) {
    const response = await fetch(`${BASE_URL}/create-business-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create business account');
    }

    return response.json();
  },

  /**
   * Update access token
   */
  async updateAccessToken(data: UpdateAccessTokenData) {
    const response = await fetch(`${BASE_URL}/update-access-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update access token');
    }

    return response.json();
  },

  /**
   * Delink business account from Meta OAuth
   */
  async unlinkBusinessAccount(accountId: string) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/unlink-business-account/${accountId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to delink business account');
    }

    return response.json();
  },

  /**
   * Get templates
   */
  async getTemplates(params: GetTemplatesParams) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    const response = await fetch(`${BASE_URL}/templates?${queryParams.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get templates');
    }

    return response.json();
  },

  /**
   * Get template by ID
   */
  async getTemplate(templateId: string, businessAccountId: string) {
    const response = await fetch(`${BASE_URL}/templates/${templateId}?businessAccountId=${businessAccountId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get template');
    }

    return response.json();
  },


  /**
   * Create template
   */
  async createTemplate(data: any) {
    const response = await fetch(`${BASE_URL}/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create template');
    }

    return response.json();
  },

  /**
   * Delete template
   */
  async deleteTemplate(templateId: string, businessAccountId: string) {
    const response = await fetch(`${BASE_URL}/templates/${templateId}?businessAccountId=${businessAccountId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to delete template');
    }

    return response.json();
  },

  /**
   * Upload media
   */
  async uploadMedia(data: UploadMediaData) {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('businessAccountId', data.businessAccountId);
    if (data.type) {
      formData.append('type', data.type);
    }

    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/media/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload media');
    }

    return response.json();
  },

  /**
   * Upload WhatsApp business profile logo
   */
  async uploadBusinessProfileLogo(accountId: string, file: File) {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('logo', file);

    const response = await fetch(`${BASE_URL}/business-profile/${accountId}/logo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload business profile logo');
    }

    return response.json() as Promise<UploadBusinessProfileLogoResponse>;
  },

  /**
   * Upload media from URL
   * Downloads the media from URL and uploads it to WhatsApp
   */
  async uploadMediaUrl(data: UploadMediaUrlData) {
    // First, download the media from the URL
    const mediaResponse = await fetch(data.url);
    if (!mediaResponse.ok) {
      throw new Error(`Failed to download media from URL: ${mediaResponse.statusText}`);
    }

    const mediaBlob = await mediaResponse.blob();
    const file = new File([mediaBlob], data.filename || 'media', { type: mediaBlob.type });

    // Then upload using the regular media upload endpoint
    return this.uploadMedia({
      file,
      businessAccountId: data.businessAccountId,
      type: data.type
    });
  },

  /**
   * Create resumable upload session
   */
  async createResumableUploadSession(data: {
    businessAccountId: string;
    fileLength: number;
    fileType: string;
    fileName: string;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/create-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create upload session');
    }

    return response.json();
  },

  /**
   * Upload file data to resumable upload session
   */
  async uploadFileData(data: {
    businessAccountId: string;
    uploadId: string;
    fileData: ArrayBuffer;
    fileOffset?: number;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/upload-data`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/octet-stream',
        'file-offset': (data.fileOffset || 0).toString(),
      },
      body: data.fileData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload file data');
    }

    return response.json();
  },

  /**
   * Query upload status
   */
  async queryUploadStatus(data: {
    businessAccountId: string;
    uploadId: string;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to query upload status');
    }

    return response.json();
  },

  /**
   * Upload media using resumable upload API and return handle
   */
  async uploadMediaWithResumableAPI(data: {
    businessAccountId: string;
    mediaUrl: string;
    mediaType: string;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/upload-media`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload media with resumable API');
    }

    return response.json();
  },

  /**
   * Get WhatsApp business profile details
   */
  async getBusinessProfile(accountId: string) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/business-profile/${accountId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to fetch business profile');
    }

    return response.json();
  },

  /**
   * Update WhatsApp business profile details
   */
  async updateBusinessProfile(accountId: string, data: BusinessProfileUpdatePayload) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/business-profile/${accountId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update business profile');
    }

    return response.json();
  },

  /**
   * Get the conversational automation config (welcome message, ice breakers,
   * commands) for the account's WhatsApp phone number.
   */
  async getConversationalAutomation(accountId: string) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/conversational-automation/${accountId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to fetch conversational automation');
    }

    return response.json();
  },

  /**
   * Update the conversational automation config. Meta replaces each field it
   * receives wholesale, so callers must send the complete `prompts` /
   * `commands` list they want live — an empty array clears that field.
   */
  async updateConversationalAutomation(accountId: string, data: ConversationalAutomationPayload) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/conversational-automation/${accountId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update conversational automation');
    }

    return response.json();
  },

};
